import { FetchesData } from "kinnect-mixins";
import {
	KColorSwatchPicker,
	KDataTable,
	KForm,
	KImageManager,
	KSignableInput
} from "kinnect-general";
import {
	VAlert,
	VCard,
	VCardActions,
	VDivider,
	VSpacer
} from "vuetify/lib";
import {
	YardTrip,
	YardTripDeliveryNoteImage
} from "kinnect-models";
import { formatDate } from "kinnect-formatters";
import { handleServerError } from "kinnect-helpers";
import { objectToFormData } from "object-to-formdata";

export default KForm.extend( {
	name: "KDeliveryNoteForm",

	props: { deliveryNote: Object },

	mixins: [ FetchesData ],

	data: () => ( {
		images: [],
		imagesLoading: false,
		formFields: {
			idUserVerifiedEIN: null,
			idUserVerifiedProductLoaded: null,
			idUserVerifiedProductMatch: null,
			salesOrdersSchedules: [],
			schedules: []
		},
		verificationFieldKeys: [
			"idUserVerifiedEIN",
			"idUserVerifiedProductLoaded",
			"idUserVerifiedProductMatch"
		],
		infoTableHeaders: [
			{
				sortable: false,
				text: "Delivery Date",
				value: "deliveryDate"
			},
			{
				sortable: false,
				text: "Driver",
				value: "driverNames"
			},
			{
				sortable: false,
				text: "Truck",
				value: "truck.name"
			}
		]
	} ),

	computed: {
		user() {
			return this.$store.getters["auth/user"];
		},

		internalRedirect() {
			if ( this.redirect ) {
				return this.redirect;
			}

			return [ "back", {
				name: "yardTrip",
				params: { idTrip: this.deliveryNote.idTrip }
			} ];
		},

		tripModel() {
			return new YardTrip( { id: this.deliveryNote.idTrip } );
		},

		schedulesTableHeaders() {
			return [
				{
					sortable: false,
					text: "Type",
					value: "type"
				},
				{
					sortable: false,
					text: "Number",
					value: "number"
				}
			];
		},

		schedulesTableWithSigsHeaders() {
			return [
				...this.schedulesTableHeaders,
				{
					sortable: false,
					text: "Signature",
					value: "formField"
				}
			];
		},

		showEINSignature() {
			return this.deliveryNote.trip.schedules.some( schedule =>
				schedule.scheduleTypes.some( st => st.requiresEINSignature )
			);
		},

		showProductMatchSignature() {
			return this.deliveryNote.trip.schedules.some( schedule =>
				schedule.scheduleTypes.some( st => st.requiresProductMatchSignature )
			);
		},

		showProductLoadedSignature() {
			return this.deliveryNote.trip.schedules.some( schedule =>
				schedule.scheduleTypes.some( st => st.requiresProductLoadedSignature )
			);
		},

		numVerificationFields() {
			return this.showEINSignature + this.showProductMatchSignature + this.showProductLoadedSignature;
		},

		schedulesIdToIndexMap() {
			const idToIndexMap = {};

			this.formFields.schedules.forEach( ( so, index ) => {
				idToIndexMap[so.id] = index;
			} );

			return idToIndexMap;
		},

		itemsBySchedule() {
			const itemsBySchedule = {};

			this.deliveryNote.trip.schedules.forEach( schedule => {
				const items = [];

				schedule.salesOrders.forEach( so => {
					const item = {
						id: `SO# ${so.id}`,
						number: so.salesOrderNumber,
						type: "SO#",
						userSignedBy: this.getSoSignedBy(
							so.id,
							schedule.id
						)
					};

					if ( schedule.scheduleTypes.some( st => st.requiresSalesOrderSignature ) ) {
						item.formField = this.getSoScheduleFormField(
							so.id,
							schedule.id
						);
					}

					items.push( item );
				} );

				schedule.purchaseOrders.forEach( po =>
					items.push( {
						id: `PO# ${po.id}`,
						number: po.purchaseOrderNumber,
						type: "PO#"
					} )
				);

				itemsBySchedule[schedule.id] = items;
			} );

			return itemsBySchedule;
		},

		headersBySchedule() {
			const headersBySchedule = {};

			Object.keys( this.itemsBySchedule ).forEach( idSchedule => {
				headersBySchedule[idSchedule] = this.itemsBySchedule[idSchedule].some( item => item.formField )
					? this.schedulesTableWithSigsHeaders
					: this.schedulesTableHeaders;
			} );

			return headersBySchedule;
		},

		numSignatureFields() {
			return [].concat( ...Object.values( this.itemsBySchedule ) ).reduce(
				( total, current ) => total += Boolean( current.formField ),
				0
			);
		},

		numSignatureFieldsSigned() {
			return this.formFields.salesOrdersSchedules.reduce(
				( total, current ) => total += Boolean( current.idUserSignedBy ),
				0
			);
		},

		numVerificationsSigned() {
			return this.verificationFieldKeys.reduce(
				( total, current ) => total += Boolean( this.formFields[current] ),
				0
			);
		},

		genStatusAlerts() {
			return [
				Boolean( this.numSignatureFields ) && <VAlert
					class="hidden-print-only"
					type={this.numSignatureFieldsSigned === this.numSignatureFields ? "success" : "error"}
				>
					All SO# Signed ({this.numSignatureFieldsSigned} / {this.numSignatureFields})
				</VAlert>,

				Boolean( this.numVerificationFields ) && <VAlert
					class="hidden-print-only"
					type={this.numVerificationsSigned === this.numVerificationFields ? "success" : "error"}
				>
					All Verifications Signed ({this.numVerificationsSigned} / {this.numVerificationFields})
				</VAlert>
			];
		}
	},

	watch: {
		deliveryNote: {
			handler( deliveryNote ) {
				if ( deliveryNote ) {
					this.verificationFieldKeys.forEach( key => {
						if ( typeof deliveryNote[key] !== "undefined" ) {
							this.formFields[key] = deliveryNote[key];
						}
					} );

					this.formFields.salesOrdersSchedules = deliveryNote.salesOrdersSchedules.map( so => ( {
						sales_order_id: so.sales_order_id,
						schedule_id: so.schedule_id,
						idUserSignedBy: so.idUserSignedBy || null
					} ) );

					this.formFields.schedules = deliveryNote.trip.schedules.map( schedule => ( {
						id: schedule.id,
						assignedColor: schedule.assignedColor
					} ) );
				}
			},
			immediate: true
		}
	},

	methods: {
		_makeTableTitleString( schedule ) {
			let title = schedule.scheduleTypeNames;

			if ( schedule.customer && schedule.customer.name ) {
				title += ` for ${schedule.customer.name}`;
			}

			title += ` at ${schedule.address.address}, ${schedule.address.city}, ${schedule.address.state} ${schedule.address.zip}`;

			return title;
		},

		getSoScheduleFormField( idSalesOrder, idSchedule ) {
			return this.formFields.salesOrdersSchedules.find( so =>
				so.sales_order_id === idSalesOrder && so.schedule_id === idSchedule
			);
		},

		getSoSignedBy( idSalesOrder, idSchedule ) {
			return this.deliveryNote.salesOrdersSchedules.find( so =>
				so.sales_order_id === idSalesOrder && so.schedule_id === idSchedule
			).userSignedBy;
		},

		async onAddImages( images ) {
			try {
				this.imagesLoading = true;

				await this.$http.post(
					new YardTripDeliveryNoteImage().for(
						this.tripModel
					)._fromResource,
					objectToFormData( { images } ),
					{ headers: { "Content-Type": "multipart/form-data" } }
				)
				.then( response => this.images.push( ...response.data ) );
			} catch ( e ) {
				handleServerError( e );
			} finally {
				this.imagesLoading = false;
			}
		},

		async onDeleteImage( image ) {
			try {
				this.imagesLoading = true;

				await new YardTripDeliveryNoteImage( { id: image.id } )
				.for( this.tripModel )
				.delete()
				.then( () =>
					this.$delete(
						this.images,
						this.images.findIndex( item => image.id === item.id )
					)
				);
			} finally {
				this.imagesLoading = false;
			}
		},

		async fetchImages() {
			this.imagesLoading = true;

			this.images = await new YardTripDeliveryNoteImage()
			.for( this.tripModel )
			.get();

			this.imagesLoading = false;
		},

		async fetch() {
			await this.fetchImages();
		}
	},

	render() {
		return <KForm
			attrs={{
				...this.$props,
				action: `yard/trips/${this.deliveryNote.idTrip}/delivery-note`,
				data: this.formFields,
				method: "put",
				redirect: this.internalRedirect
			}}
			on={this.$listeners}
		>
			<VCard class="mb-4" outlined>
				<KDataTable
					headers={this.infoTableHeaders}
					hideDefaultFooter
					items={[ this.deliveryNote.trip ]}
					itemsPerPage={-1}
					scopedSlots={{
						"item.deliveryDate": ( { item } ) => formatDate( item.deliveryDate )
					}}
				></KDataTable>
			</VCard>

			{this.deliveryNote.trip.schedules.map( schedule =>
				<VCard class="mb-4" outlined>
					<KDataTable
						headers={this.headersBySchedule[schedule.id]}
						hideDefaultFooter
						items={this.itemsBySchedule[schedule.id]}
						itemsPerPage={-1}
						noDataText="There are no SO# or PO# for this schedule"
						scopedSlots={{
							"item.formField": ( { item } ) =>
								item.formField && <KSignableInput
									class="my-2 ml-2"
									immutable
									selectProps={{
										itemText: "name",
										itemValue: "id",
										label: "Driver: I verify that I have the invoice corresponding to this sales order number.",
										hideDetails: true
									}}
									userSignedBy={item.userSignedBy}
									vModel={item.formField.idUserSignedBy}
								></KSignableInput>
						}}
					>
						<template slot="title">
							{this._makeTableTitleString( schedule )}
						</template>

						<template slot="subtitle">
							Notes: {schedule.description || "--"}
						</template>
					</KDataTable>

					<VDivider class="mb-3"></VDivider>

					<VCardActions>
						<VSpacer></VSpacer>

						<KColorSwatchPicker
							class="flex-grow-0"
							label="Assigned Color for This Schedule"
							vModel={this.formFields.schedules[this.schedulesIdToIndexMap[schedule.id]].assignedColor}
						></KColorSwatchPicker>
					</VCardActions>
				</VCard>
			)}

			{this.showEINSignature && <KSignableInput
				immutable
				selectProps={{
					itemText: "name",
					itemValue: "id",
					label: "Yard Personnel: I verify that the EIN’s on the rental agreement match the equipment being delivered."
				}}
				userSignedBy={this.deliveryNote.userVerifiedEIN}
				vModel={this.formFields.idUserVerifiedEIN}
			></KSignableInput>}

			{this.showProductMatchSignature && <KSignableInput
				immutable
				selectProps={{
					itemText: "name",
					itemValue: "id",
					label: "Warehouse Personnel: I verify that the product, and the quantities match the invoices."
				}}
				userSignedBy={this.deliveryNote.userVerifiedProductMatch}
				vModel={this.formFields.idUserVerifiedProductMatch}
			></KSignableInput>}

			{this.showProductLoadedSignature && <KSignableInput
				immutable
				selectProps={{
					itemText: "name",
					itemValue: "id",
					label: "Warehouse Personnel: I verify that the product on each invoice is loaded on the truck."
				}}
				userSignedBy={this.deliveryNote.userVerifiedProductLoaded}
				vModel={this.formFields.idUserVerifiedProductLoaded}
			></KSignableInput>}

			<KImageManager
				class="mb-4"
				images={this.images}
				itemFullHeightKey="sizes.large.height"
				itemFullKey="sizes.large.url"
				itemFullWidthKey="sizes.large.width"
				itemThumbKey="sizes.thumbnail.url"
				loading={this.imagesLoading}
				onAddImages={this.onAddImages}
				onDeleteImage={this.onDeleteImage}
				thumbHeight={150}
				thumbWidth={150}
				title="Add Images"
			></KImageManager>

			{this.genStatusAlerts}
		</KForm>;
	}
} );
